import { useEffect, useMemo, useState } from 'react';

/**
 * Time remaining until `targetIso`, recomputed once a second while mounted.
 *
 * Mirrors the interval/visibility pattern of `HeaderLiveClock` in
 * ExecutiveAppHeader: a background tab throttles or suspends timers, so the
 * `visibilitychange` resync is what stops the display coming back stale after
 * the tab has been away for a while.
 *
 * Lives in the feature rather than the management component so the user-facing
 * display surface can reuse it without duplicating the tick.
 *
 * @param {string|Date|null} targetIso The target moment, in UTC.
 * @returns {{days:number, hours:number, minutes:number, seconds:number,
 *            totalMs:number, elapsed:boolean, valid:boolean}}
 */
export function useCountdown(targetIso) {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const sync = () => setNow(Date.now());
        sync();
        const id = window.setInterval(sync, 1000);
        const onVisibility = () => {
            if (document.visibilityState === 'visible') sync();
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.clearInterval(id);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);

    return useMemo(() => {
        const targetMs = targetIso ? new Date(targetIso).getTime() : NaN;
        if (!Number.isFinite(targetMs)) {
            return {
                days: 0, hours: 0, minutes: 0, seconds: 0,
                totalMs: 0, elapsed: false, valid: false,
            };
        }

        // Clamped at zero: once the moment has passed the countdown reads all
        // zeros rather than running negative.
        const remaining = Math.max(0, targetMs - now);
        const totalSeconds = Math.floor(remaining / 1000);

        return {
            days: Math.floor(totalSeconds / 86400),
            hours: Math.floor((totalSeconds % 86400) / 3600),
            minutes: Math.floor((totalSeconds % 3600) / 60),
            seconds: totalSeconds % 60,
            totalMs: remaining,
            elapsed: remaining === 0,
            valid: true,
        };
    }, [targetIso, now]);
}

export default useCountdown;
